import React, { useState, useEffect, useCallback, useRef } from 'react';
import { extractApiError } from '../../utils/apiError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSave,
  faCube,
  faRuler,
  faWeight,
  faTags,
  faBox,
  faWarehouse,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import productCategoryService from '../../services/productCategoryService';
import productCatalogueService from '../../services/productCatalogueService';
import warehouseService from '../../services/warehouseService';
import { useTranslation } from 'react-i18next';
import { validateProductSizeForm } from '../../utils/validate';
import SearchableSelect from '../common/SearchableSelect';

/**
 * ProductSizeForm Component
 * Modal form for adding/editing product sizes with validation
 */
const ProductSizeForm = ({
  isOpen,
  onClose,
  onSubmit,
  productSize = null,
  loading = false,
}) => {
  const { t } = useTranslation();
  // String constants for UI text
  const strings = t('productSizes.forms', { returnObjects: true }) || {};
  const requiredIndicator = t('common.symbols.required');

  const [formData, setFormData] = useState({
    alias_name: '',
    product_size: '',
    product_weight: '',
    standard_length: '',
    product_thickness: '',
    thickness_unit: '',
    current_stock: '0',
    minimum_stock: '0',
    product_category_id: '',
    product_catalogue_id: '',
    warehouses: [],
  });
  
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  
  const [errors, setErrors] = useState({});
  // Local submit state — the form owns its button lifecycle (reset in finally)
  // and only closes on genuine success, so a failed save no longer discards data.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [categories, setCategories] = useState([]);
  const [productCatalogues, setProductCatalogues] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [catalogueSearchLoading, setCatalogueSearchLoading] = useState(false);
  const catalogueSearchRef = useRef(null);

  // Static cache to persist data across component instances
  if (!window.productSizeFormCache) {
    window.productSizeFormCache = {
      categories: null,
      productCatalogues: null,
      categoriesLoaded: false,
      productCataloguesLoaded: false,
      loading: false
    };
  }
  const cache = window.productSizeFormCache;

  // Initialize form data when productSize prop changes
  useEffect(() => {
    // guard to prevent async updates after effect is stale
    let isActive = true;

    if (productSize) {
      setFormData({
        alias_name: productSize.alias_name || '',
        product_size: productSize.product_size || '',
        product_weight: productSize.product_weight ? productSize.product_weight.toString() : '',
        standard_length: productSize.standard_length ? productSize.standard_length.toString() : '',
        product_thickness: productSize.product_thickness || '',
        thickness_unit: productSize.thickness_unit || '',
        current_stock: productSize.current_stock ? productSize.current_stock.toString() : '0',
        minimum_stock: productSize.minimum_stock ? productSize.minimum_stock.toString() : '0',
        product_category_id: productSize.product_category_id ? productSize.product_category_id.toString() : '',
        product_catalogue_id: productSize.product_catalogue_id ? productSize.product_catalogue_id.toString() : '',
        warehouses: Array.isArray(productSize.warehouses) ? productSize.warehouses.map(w => ({
          warehouse_id: w.warehouse_id,
          stock_quantity: w.stock_quantity || 0,
          warehouse_name: w.warehouse_name || ''
        })) : [],
      });

      // capture ids as strings to avoid type mismatch
      const capturedCategoryId = productSize.product_category_id ? String(productSize.product_category_id) : null;
      const capturedProductId = productSize.product_catalogue_id ? String(productSize.product_catalogue_id) : null;

      // helper: inject or fetch category display name
      const ensureCategoryOption = async () => {
        if (!capturedCategoryId) return;

        // prefer name fields from payload
        let name = productSize.category_name || productSize.name || productSize.product_category_name || productSize.categoryName || productSize.category || '';
        // fallback: fetch by id if no name present
        if (!name) {
          try {
            setLoadingOptions(true);
            const res = await productCategoryService.getProductCategoryById(capturedCategoryId).catch(() => null);
            if (!isActive) return;
            const data = res && (res.data || res);
            name = data && (data.name || data.category_name || data.product_category_name || data.categoryName || data.title || '');
          } catch (e) {
            // ignore fetch failure
          } finally {
            if (isActive) setLoadingOptions(false);
          }
        }

        if (!name) return;

        const newCategory = { id: capturedCategoryId, name };
        if (!isActive) return;
        // inject at top if missing
        setCategories(prev => {
          const exists = prev.some(cat => String(cat.id) === String(capturedCategoryId));
          if (exists) return prev;
          return [newCategory, ...prev];
        });

        cache.categories = cache.categories || [];
        if (!cache.categories.some(c => String(c.id) === String(capturedCategoryId))) {
          cache.categories = [newCategory, ...cache.categories];
        }
      };

      // helper: inject or fetch product display name
      const ensureProductOption = async () => {
        if (!capturedProductId) return;

        let name = productSize.product_name || productSize.name || productSize.alias_name || productSize.product_catalogue_name || productSize.productName || '';
        if (!name) {
          try {
            setLoadingOptions(true);
            const res = await productCatalogueService.getProductCatalogueById(capturedProductId).catch(() => null);
            if (!isActive) return;
            const data = res && (res.data || res);
            name = data && (data.product_name || data.name || data.alias_name || data.title || '');
          } catch (e) {
            // ignore
          } finally {
            if (isActive) setLoadingOptions(false);
          }
        }

        if (!name) return;

        const newProduct = { id: capturedProductId, product_name: name, name };
        if (!isActive) return;
        setProductCatalogues(prev => {
          const exists = prev.some(p => String(p.id) === String(capturedProductId));
          if (exists) return prev;
          return [newProduct, ...prev];
        });

        cache.productCatalogues = cache.productCatalogues || [];
        if (!cache.productCatalogues.some(p => String(p.id) === String(capturedProductId))) {
          cache.productCatalogues = [newProduct, ...cache.productCatalogues];
        }
      };

      // fire both (don't await to keep UI snappy)
      void ensureCategoryOption();
      void ensureProductOption();
    } else {
      setFormData({
        alias_name: '',
        product_size: '',
        product_weight: '',
        standard_length: '',
        product_thickness: '',
        thickness_unit: '',
        current_stock: '0',
        minimum_stock: '0',
        product_category_id: '',
        product_catalogue_id: '',
        warehouses: []
      });
    }

    setErrors({});

    return () => {
      // prevent state updates from stale async work
      isActive = false;
    };
  }, [productSize, isOpen, cache]);

  const loadDropdownOptions = useCallback(async () => {
    // debug logs removed

    // Check if we already have cached data - avoid API calls if data exists
    if (cache.categoriesLoaded && cache.productCataloguesLoaded) {
      if (categories.length === 0 && cache.categories) {
        setCategories(cache.categories);
      }
      if (productCatalogues.length === 0 && cache.productCatalogues) {
        setProductCatalogues(cache.productCatalogues);
      }
      return;
    }

    // Prevent multiple simultaneous calls
    if (cache.loading) {
      return;
    }
    
    cache.loading = true;
    setLoadingOptions(true);
    
    try {
      const needCategories = !cache.categoriesLoaded;
      const needProductCatalogues = !cache.productCataloguesLoaded;

  // debug logs removed

      const promises = [];
      
      if (needCategories) {
        // Use productCategoryService to fetch categories
        promises.push(
          productCategoryService.getProductCategories()
            .catch(() => ({ success: false, data: [] }))
        );
      } else {
        promises.push(Promise.resolve({ success: true, data: cache.categories }));
      }

      // HSN codes removed - skip

      if (needProductCatalogues) {
        // Use productCatalogueService to fetch product catalogues
        promises.push(
          productCatalogueService.getAllProducts()
            .catch(() => ({ success: false, data: [] }))
        );
      } else {
        promises.push(Promise.resolve({ success: true, data: cache.productCatalogues }));
      }

  const [categoriesResult, productCataloguesResult] = await Promise.all(promises);

      // Helper to normalize different API shapes: plain array or { success, data }
      const extractArray = (res) => {
        if (!res) return [];
        // If API returned the array directly
        if (Array.isArray(res)) return res;
        // If API returned { data: [...] }
        if (res.data && Array.isArray(res.data)) return res.data;
        // If API returned { result: [...] }
        if (res.result && Array.isArray(res.result)) return res.result;
        // If API returned { success: true, data: [...] }
        if (res.success && res.data && Array.isArray(res.data)) return res.data;
        // Fallback to empty
        return [];
      };

      const categoriesData = extractArray(categoriesResult);
      const productCataloguesData = extractArray(productCataloguesResult);

    // debug logs removed

      if (categoriesData.length > 0) {
        // Merge fetched data with any existing items (e.g., injected from edit mode)
        setCategories(prev => {
          const merged = [...categoriesData];
          prev.forEach(existing => {
            if (!merged.some(cat => cat.id === existing.id)) {
              merged.push(existing);
            }
          });
          return merged;
        });
        cache.categories = categoriesData;
        cache.categoriesLoaded = true;
      }

      // HSN removed

      if (productCataloguesData.length > 0) {
        // Merge fetched data with any existing items (e.g., injected from edit mode)
        setProductCatalogues(prev => {
          const merged = [...productCataloguesData];
          prev.forEach(existing => {
            if (!merged.some(prod => prod.id === existing.id)) {
              merged.push(existing);
            }
          });
          return merged;
        });
        cache.productCatalogues = productCataloguesData;
        cache.productCataloguesLoaded = true;
      } else {
        // Still mark as loaded even if empty to avoid infinite loops
        cache.productCataloguesLoaded = true;
        cache.productCatalogues = [];
      }
    } catch (error) {
      // Reset loading flags on error to allow retry
      cache.loading = false;
      setLoadingOptions(false);
    } finally {
      cache.loading = false;
      setLoadingOptions(false);
    }
  }, [ cache,categories,productCatalogues]);


  // Load dropdown options when form opens, but use cached data when available
  useEffect(() => {
    if (isOpen) {
      // First check if we have cached data and populate local state
      if (cache.categories && categories.length === 0) {
        setCategories(cache.categories);
      }
      if (cache.productCatalogues && productCatalogues.length === 0) {
        setProductCatalogues(cache.productCatalogues);
      }

      // Only load if we don't have cached data and not already loading
      if ((!cache.categoriesLoaded || !cache.productCataloguesLoaded) && !cache.loading) {
        loadDropdownOptions();
      }
    }
  }, [
    isOpen,
    loadDropdownOptions,
    cache.categories,
    cache.categoriesLoaded,
    cache.productCatalogues,
    cache.productCataloguesLoaded,
    cache.loading,
    productCatalogues.length,
    categories.length,
  ]);

  // Fetch warehouses
  useEffect(() => {
    if (isOpen) {
      const fetchW = async () => {
        setLoadingWarehouses(true);
        try {
          const res = await warehouseService.getAllWarehouses();
          if (res.success) {
            setAvailableWarehouses(Array.isArray(res.data) ? res.data : []);
          }
        } catch (e) {
          console.error('Error fetching warehouses:', e);
        } finally {
          setLoadingWarehouses(false);
        }
      };
      fetchW();
    }
  }, [isOpen]);

  const handleAddWarehouse = (warehouseId) => {
    if (!warehouseId) return;
    const warehouse = availableWarehouses.find(w => String(w.id) === String(warehouseId));
    if (!warehouse) return;

    if (formData.warehouses.some(w => String(w.warehouse_id) === String(warehouseId))) {
      return; // Already added
    }

    setFormData(prev => ({
      ...prev,
      warehouses: [...prev.warehouses, {
        warehouse_id: parseInt(warehouseId),
        stock_quantity: '',
        warehouse_name: warehouse.name
      }]
    }));
  };

  const handleRemoveWarehouse = (warehouseId) => {
    setFormData(prev => ({
      ...prev,
      warehouses: prev.warehouses.filter(w => String(w.warehouse_id) !== String(warehouseId))
    }));
  };

  const handleWarehouseStockChange = (warehouseId, quantity) => {
    setFormData(prev => ({
      ...prev,
      warehouses: prev.warehouses.map(w => 
        String(w.warehouse_id) === String(warehouseId) 
          ? { ...w, stock_quantity: quantity === '' ? '' : parseFloat(quantity) || 0 }
          : w
      )
    }));
  };

  // When options load, if productSize provides a name but formData doesn't have a matching id,
  // try to find the option by name and set the id so the controlled <select> can display it.
  useEffect(() => {
    if (!productSize) return;

    // Sync category by name -> id if needed
    try {
      if (categories && categories.length > 0) {
        const hasSelectedId = formData.product_category_id && categories.some(c => String(c.id) === String(formData.product_category_id));
        if (!hasSelectedId && productSize.category_name) {
          const target = String(productSize.category_name || '').trim().toLowerCase();
          const match = categories.find(c => String((c.name || c.category_name || '')).trim().toLowerCase() === target);
          if (match) {
            setFormData(prev => ({ ...prev, product_category_id: String(match.id) }));
          }
        }
      }

      // Sync product catalogue by name -> id if needed
      if (productCatalogues && productCatalogues.length > 0) {
        const hasSelectedProdId = formData.product_catalogue_id && productCatalogues.some(p => String(p.id) === String(formData.product_catalogue_id));
        if (!hasSelectedProdId && productSize.product_name) {
          const targetP = String(productSize.product_name || '').trim().toLowerCase();
          const matchP = productCatalogues.find(p => String((p.product_name || p.name || p.alias_name || '')).trim().toLowerCase() === targetP);
          if (matchP) {
            setFormData(prev => ({ ...prev, product_catalogue_id: String(matchP.id) }));
          }
        }
      }
    } catch (e) {
      // swallow any errors from matching
    }
  }, [formData.product_catalogue_id, formData.product_category_id,categories, productCatalogues,productSize]);

  const handleCatalogueSearch = useCallback((term) => {
    if (catalogueSearchRef.current) clearTimeout(catalogueSearchRef.current);
    catalogueSearchRef.current = setTimeout(async () => {
      setCatalogueSearchLoading(true);
      try {
        const result = await productCatalogueService.getProductCatalogue({ search: term, limit: 20, skip: 0 });
        if (result.success && Array.isArray(result.data)) {
          setProductCatalogues(prev => {
            const merged = [...result.data];
            // keep currently selected item if not in results
            if (formData.product_catalogue_id) {
              const exists = merged.some(p => String(p.id) === String(formData.product_catalogue_id));
              if (!exists) {
                const current = prev.find(p => String(p.id) === String(formData.product_catalogue_id));
                if (current) merged.unshift(current);
              }
            }
            return merged;
          });
        }
      } catch (e) {
        // ignore
      } finally {
        setCatalogueSearchLoading(false);
      }
    }, 400);
  }, [formData.product_catalogue_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Handle numeric fields
    if (['product_weight', 'standard_length', 'current_stock', 'minimum_stock'].includes(name)) {
      newValue = value.replace(/[^0-9.]/g, '');
      const parts = newValue.split('.');
      if (parts.length > 2) {
        newValue = parts[0] + '.' + parts.slice(1).join('');
      }
      // Limit decimal places based on field
      if (['product_weight', 'standard_length'].includes(name) && parts[1] && parts[1].length > 3) {
        newValue = parts[0] + '.' + parts[1].substring(0, 3);
      } else if (['current_stock', 'minimum_stock'].includes(name) && parts[1]) {
        // Stock fields typically don't need decimals
        newValue = parts[0];
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = validateProductSizeForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;            // guard double-submit
    if (!validateForm()) return;

    const payload = {
      alias_name: formData.alias_name.trim(),
      product_size: formData.product_size.trim(),
      product_weight: formData.product_weight.trim() ? parseFloat(formData.product_weight) : null,
      standard_length: formData.standard_length.trim() ? parseFloat(formData.standard_length) : null,
      product_thickness: formData.product_thickness.trim() || null,
      thickness_unit: formData.thickness_unit.trim() || null,
      product_category_id: parseInt(formData.product_category_id),
      product_catalogue_id: parseInt(formData.product_catalogue_id),
      warehouses: formData.warehouses.map(w => ({
        warehouse_id: w.warehouse_id,
        stock_quantity: parseFloat(w.stock_quantity) || 0
      }))
    };

    setSubmitError('');
    setSubmitting(true);
    try {
      const result = await onSubmit(payload);
      // Only close on genuine success. Previously onClose() was in `finally`,
      // so a failed save silently closed the modal and discarded the entry.
      if (result && result.success) {
        onClose();
      } else {
        const rawErr = result && result.error;
        const msg = typeof rawErr === 'string' ? rawErr : (rawErr ? extractApiError(rawErr) : '');
        setSubmitError(msg || 'Could not save. Please try again.');
      }
    } catch (err) {
      setSubmitError(extractApiError(err) || 'Could not save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.product-size-form-modal{ -ms-overflow-style: none; scrollbar-width: none; } .product-size-form-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto product-size-form-modal"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >

        <div 
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <h2 
            className="text-2xl font-bold"
            style={{ color: UI_COLORS.TEXT.PRIMARY }}
          >
            {productSize ? strings.title?.edit : strings.title?.add}
          </h2>
          {/* Debug button - remove in production */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ 
                color: UI_COLORS.TEXT.MUTED,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1)
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
              }}
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>
          </div>
        </div>


        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Alias Name */}
          <div className="space-y-2">
            <label 
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faCube} className="mr-2" />
              {`${strings.fields?.alias_name?.label} ${requiredIndicator}`}
            </label>
            <input
              type="text"
              name="alias_name"
              value={formData.alias_name}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.alias_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.alias_name && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
              placeholder={strings.fields?.alias_name?.placeholder}
            />
              {errors.alias_name && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.alias_name}
              </p>
            )}
          </div>

          {/* Product Size */}
          <div className="space-y-2">
            <label 
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faRuler} className="mr-2" />
              {`${strings.fields?.product_size?.label} ${requiredIndicator}`}
            </label>
            <input
              type="text"
              name="product_size"
              value={formData.product_size}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.product_size ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.product_size && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
              placeholder={strings.fields?.product_size?.placeholder}
            />
            {errors.product_size && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.product_size}
              </p>
            )}
          </div>

          {/* Weight and Standard Length Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label 
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faWeight} className="mr-2" />
                {strings.fields?.product_weight?.label}
              </label>
              <input
                type="number"
                name="product_weight"
                value={formData.product_weight}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.product_weight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.product_weight && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields?.product_weight?.placeholder}
                min="0"
                step="0.001"
              />
              {errors.product_weight && (
                <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.product_weight}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label 
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faRuler} className="mr-2" />
                {strings.fields?.standard_length?.label}
              </label>
              <input
                type="number"
                name="standard_length"
                value={formData.standard_length}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.standard_length ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.standard_length && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields?.standard_length?.placeholder}
                min="0"
                step="0.001"
              />
              {errors.standard_length && (
                <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.standard_length}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label 
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faRuler} className="mr-2" />
                {strings.fields?.product_thickness?.label}
              </label>
              <input
                type="text"
                name="product_thickness"
                value={formData.product_thickness}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.product_thickness ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.product_thickness && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields?.product_thickness?.placeholder}
              />
              {errors.product_thickness && (
                <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.product_thickness}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label 
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faTags} className="mr-2" />
                {strings.fields?.thickness_unit?.label}
              </label>
              <input
                type="text"
                name="thickness_unit"
                value={formData.thickness_unit}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                }}
                placeholder={strings.fields?.thickness_unit?.placeholder}
              />
            </div>
          </div>
          {/* Category */}
          <div className="space-y-2">
            <label 
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faTags} className="mr-2" />
              {`${strings.fields?.category_id?.label} ${requiredIndicator}`}
            </label>
            <select
              name="product_category_id"
              value={formData.product_category_id}
              onChange={handleChange}
              disabled={loadingOptions}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.product_category_id ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.product_category_id && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
            >
              <option value="">{strings.fields?.category_id?.placeholder}</option>
              {categories.map(category => {
                return (
                  <option
                    key={category.id}
                    value={category.id}
                  >
                    {category.name || category.category_name}
                  </option>
                );
              })}
            </select>
            {errors.product_category_id && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.product_category_id}
              </p>
            )}
          </div>

          {/* Product Catalogue Selection */}
          <div className="space-y-2">
            <label
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faBox} className="mr-2" />
              {`${strings.fields?.product_catalogue_id?.label} ${requiredIndicator}`}
            </label>
            <SearchableSelect
              name="product_catalogue_id"
              value={formData.product_catalogue_id}
              onChange={handleChange}
              disabled={loadingOptions}
              loading={catalogueSearchLoading}
              placeholder={loadingOptions ? 'Loading...' : strings.fields?.product_catalogue_id?.placeholder || 'Select product...'}
              error={errors.product_catalogue_id}
              onSearchChange={handleCatalogueSearch}
              options={productCatalogues.map(p => ({
                value: p.id,
                label: p.product_name || p.name,
              }))}
            />
            {errors.product_catalogue_id && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.product_catalogue_id}
              </p>
            )}
          </div>

          {/* Warehouse Inventory Section (Only for Add Mode) */}
          {!productSize && (
            <div className="space-y-4 pt-4 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <div className="flex items-center justify-between">
                <label 
                  className="text-base font-bold"
                  style={{ color: UI_COLORS.TEXT.PRIMARY }}
                >
                  <FontAwesomeIcon icon={faWarehouse} className="mr-2" />
                  Warehouse Inventory
                </label>
                
                <div className="flex items-center gap-2 min-w-[200px]">
                  <SearchableSelect
                    options={availableWarehouses
                      .filter(hw => !formData.warehouses.some(w => String(w.warehouse_id) === String(hw.id)))
                      .map(hw => ({ value: hw.id, label: hw.name }))}
                    onChange={(e) => handleAddWarehouse(e.target.value)}
                    placeholder="Add warehouse..."
                    loading={loadingWarehouses}
                    value=""
                    className="w-full"
                  />
                </div>
              </div>

              <style>
                {`
                  .no-spinners::-webkit-outer-spin-button,
                  .no-spinners::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                  }
                  .no-spinners {
                    -moz-appearance: textfield;
                  }
                `}
              </style>

              {formData.warehouses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.warehouses.map((w, idx) => (
                    <div 
                      key={w.warehouse_id} 
                      className="p-4 rounded-xl border-2 transition-all duration-200 flex flex-col gap-3"
                      style={{ 
                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
                        borderColor: errors[`warehouses.${idx}.stock_quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.BORDERS.LIGHT 
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                          {w.warehouse_name || `Warehouse #${w.warehouse_id}`}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveWarehouse(w.warehouse_id)}
                          className="text-sm p-1.5 rounded-lg transition-colors hover:bg-red-50"
                          style={{ color: UI_COLORS.STATUS.ERROR }}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                      <div>
                        <label className="text-xs font-semibold mb-1 block" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                          Stock Quantity
                        </label>
                        <input
                          type="number"
                          value={w.stock_quantity}
                          onChange={(e) => handleWarehouseStockChange(w.warehouse_id, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className="w-full px-3 py-2 border-2 rounded-lg text-sm focus:ring-2 transition-all duration-200 no-spinners"
                          style={{
                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                            borderColor: errors[`warehouses.${idx}.stock_quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                            color: UI_COLORS.FORMS.INPUT.text,
                          }}
                          placeholder="0"
                          min="0"
                        />
                        {errors[`warehouses.${idx}.stock_quantity`] && (
                          <p className="mt-1 text-[10px] font-medium text-red-500">
                            {errors[`warehouses.${idx}.stock_quantity`]}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div 
                  className="p-6 rounded-xl border-2 border-dashed text-center"
                  style={{ 
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.3),
                    borderColor: UI_COLORS.BORDERS.LIGHT 
                  }}
                >
                  <div className="text-gray-400 text-sm italic">
                    No warehouses selected. Use the dropdown above to add warehouses and their stock.
                  </div>
                </div>
              )}
            </div>
          )}

          {submitError && (
            <div
              className="rounded-xl px-4 py-3 text-sm font-medium mt-4"
              role="alert"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
                color: UI_COLORS.STATUS.ERROR,
                border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}`,
              }}
            >
              {submitError}
            </div>
          )}

          {/* Submit Buttons — key off the form's own `submitting` so they can't
              be left stuck by external state, and closing happens only on success. */}
          <div className="flex justify-end space-x-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                color: UI_COLORS.BUTTONS.SECONDARY.text,
                border: `2px solid ${UI_COLORS.BUTTONS.SECONDARY.border}`
              }}
            >
              {strings.buttons?.cancel}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
                border: `2px solid ${UI_COLORS.BUTTONS.PRIMARY.background}`
              }}
            >
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {submitting
                ? (productSize ? strings.buttons?.submit?.loading?.edit : strings.buttons?.submit?.loading?.add)
                : (productSize ? strings.buttons?.submit?.edit : strings.buttons?.submit?.add)
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductSizeForm;
